import type { QueryClient } from "@tanstack/react-query";
import type {
  UniverseFactNode,
  UniverseGraph,
  UniverseGraphEdge,
} from "@/types/brandUniverse.type";

/**
 * Cache surgery for the universe graph, the one entry the stage, the orbit
 * index and the maturity meter all read. Shared by optimistic mutations and by
 * realtime events, so a fact leaves the screen the same way whether this tab or
 * a teammate deleted it.
 */

export const brandUniverseGraphKey = (brandId: string | null) =>
  ["brandUniverseGraph", brandId] as const;

/** What `removeFactsFromGraph` took out, kept for a narrow rollback. */
export type RemovedFacts = { nodes: UniverseFactNode[]; edges: UniverseGraphEdge[] };

/** Keep an in-flight refetch from landing on top of an optimistic write. */
export function cancelGraphFetches(qc: QueryClient, brandId: string) {
  return qc.cancelQueries({ queryKey: brandUniverseGraphKey(brandId) });
}

/** Drops the facts and every edge touching them. */
export function removeFactsFromGraph(
  qc: QueryClient,
  brandId: string,
  factIds: string[],
): RemovedFacts {
  const ids = new Set(factIds);
  const removed: RemovedFacts = { nodes: [], edges: [] };
  qc.setQueryData<UniverseGraph>(brandUniverseGraphKey(brandId), (graph) => {
    if (!graph) return graph;
    const nodes = graph.nodes.filter((node) => {
      if (node.type !== "fact" || !ids.has(node.id)) return true;
      removed.nodes.push(node);
      return false;
    });
    if (removed.nodes.length === 0) return graph;
    const edges = graph.edges.filter((edge) => {
      if (!ids.has(edge.sourceId) && !ids.has(edge.targetId)) return true;
      removed.edges.push(edge);
      return false;
    });
    return { ...graph, nodes, edges };
  });
  return removed;
}

/**
 * Puts back what `removeFactsFromGraph` took, skipping facts a refetch already
 * restored and edges whose other end has since gone.
 */
export function restoreFactsToGraph(qc: QueryClient, brandId: string, removed: RemovedFacts) {
  if (removed.nodes.length === 0) return;
  qc.setQueryData<UniverseGraph>(brandUniverseGraphKey(brandId), (graph) => {
    if (!graph) return graph;
    const present = new Set(graph.nodes.map((node) => node.id));
    const nodes = removed.nodes.filter((node) => !present.has(node.id));
    if (nodes.length === 0) return graph;
    for (const node of nodes) present.add(node.id);
    const edges = removed.edges.filter(
      (edge) => present.has(edge.sourceId) && present.has(edge.targetId),
    );
    return {
      ...graph,
      nodes: [...graph.nodes, ...nodes],
      edges: [...graph.edges, ...edges],
    };
  });
}
